/**
 * Tách lời văn của trợ lý thành khối để vẽ — phần thuần của `components/ai/LoiVan.tsx` native.
 *
 * Lời nhắc giới hạn lời văn ở đoạn cách dòng trắng và in đậm vài con số; không kéo thư viện markdown vào để vẽ hai dấu
 * sao (là nhận cả bề mặt chèn HTML). Ở đây chỉ tách chuỗi; component trả nút React, không có đường nào cho thẻ.
 *
 * Nhãn `NHAN` và `NGUON` là **chữ của máy chủ** (`render_nhan`, `render_nguon`), không phải chuỗi giao diện: chúng phải
 * TRÙNG KHỚP bản máy chủ dựng lúc chốt `done`, nếu không chữ nhảy hình khi bản chốt về. Vì vậy chúng nằm ở đây, không
 * đi qua catalog dịch.
 */

export const NHAN: Record<string, string> = {
  dudoan: '🔮 Dự đoán',
  nhandinh: '💭 Nhận định',
};

export const NGUON: Record<string, string> = {
  so_lieu: 'Theo số liệu',
  tai_lieu: 'Theo tài liệu',
  web: 'Theo trang web của tổ chức',
};

/** Dòng kẻ ngang markdown: `---` · `***` · `___`. */
const KE_NGANG = /^\s*([-*_])\1{2,}\s*$/;
/** Một hàng bảng markdown: `| a | b |`. */
const HANG_BANG = /^\s*\|.*\|\s*$/;
/** Dòng NGĂN của bảng: `|:---|---:|`. */
const NGAN_BANG = /^\s*\|?[\s:|-]+\|[\s:|-]*$/;
/** Gạch đầu dòng: đòi khoảng trắng SAU đúng một dấu, nên `**Hải Phòng:**` không khớp. */
const GACH_DAU_DONG = /^[ \t]*[-*+][ \t]+/;
/** Tiêu đề markdown `## X` — bỏ dấu thăng, giữ chữ. */
const TIEU_DE = /^\s{0,3}#{1,6}\s+/;
/**
 * Địa chỉ web — CHỈ `http`/`https`: bắt `javascript:` hay `data:` là dựng đường chèn mã ngay giữa thứ duy nhất ở màn
 * này đến từ mô hình. Ngoặc đơn nằm ngoài tập ký tự, để `(xem https://a.vn/b)` không thành liên kết 404.
 */
const LIEN_KET = /(https?:\/\/[^\s<>()[\]{}"'`]+)/g;
/** Dấu câu dính đuôi địa chỉ — thuộc về câu, không thuộc địa chỉ. */
const DUOI_CAU = /[.,;:!?…"'»]+$/;

/**
 * `{{nhandinh: X}}` → `💭 Nhận định — X`. Máy chủ chỉ dựng nhãn MỘT LẦN lúc chốt `done`; các mẩu `van` chảy ra trước đó
 * là văn thô. Hàm BẤT BIẾN VỚI SỐ LẦN ÁP, nên đường đang chảy và đường đã chốt dùng chung một nhánh.
 */
export function dungNhan(s: string): string {
  return (
    s
      .replace(
        /\{\{\s*(dudoan|nhandinh)\s*:\s*([\s\S]*?)\}\}/gi,
        (_m, loai: string, noi: string) => `\n\n${NHAN[loai.toLowerCase()]} — ${noi.trim()}`,
      )
      // Nhãn ĐANG MỞ ở cuối chuỗi: dựng luôn thay vì giấu — giấu thì chữ đứng khựng lại giữa chừng.
      .replace(
        /\{\{\s*(dudoan|nhandinh)\s*:\s*([\s\S]*)$/i,
        (_m, loai: string, noi: string) => `\n\n${NHAN[loai.toLowerCase()]} — ${noi.trimStart()}`,
      )
      // Dấu mở còn dở dang (`{{nhandi`): lời văn không bao giờ chứa hai ngoặc nhọn vì việc gì khác.
      .replace(/\{\{[^{}]*$/, '')
  );
}

/** `{{nguon: tai_lieu}}` → dòng in đậm, hoặc bỏ hẳn khi dưới hai nhãn — cùng luật `render_nguon` phía máy chủ. */
export function dungNguon(s: string): string {
  const dem = (s.match(/\{\{\s*nguon\s*:\s*(so_lieu|tai_lieu|web)\s*\}\}/gi) || []).length;
  const bo = (x: string) => x.replace(/\{\{\s*nguon\s*:\s*(?:so_lieu|tai_lieu|web)\s*\}\}/gi, '');
  if (dem < 2) return bo(s);
  return s.replace(
    /\{\{\s*nguon\s*:\s*(so_lieu|tai_lieu|web)\s*\}\}/gi,
    (_m, loai: string) => `\n\n**${NGUON[loai.toLowerCase()]}**\n\n`,
  );
}

/** Khối của một đoạn: bảng (lỡ sinh ra dù lời nhắc cấm), cụm liệt kê, hoặc các dòng chữ. */
export type KhoiDoan =
  | { loai: 'bang'; dau: string[]; than: string[][] }
  | { loai: 'cum'; cum: { gach: boolean; dong: string[] }[] }
  | { loai: 'chu'; dong: string[] };

/** Lời văn → các đoạn (tách theo dòng trắng). */
export function tachDoan(text: string): string[] {
  return dungNguon(dungNhan(text || ''))
    .split(/\n\s*\n/)
    .filter((d) => d.trim());
}

/**
 * Một đoạn → khối. Giữ xuống dòng đơn (`<p>` gộp khoảng trắng, ba mươi hàng bảng dính thành một dòng), dựng bảng thật
 * khi nhận ra bảng, và gom liệt kê theo CỤM liền nhau — câu dẫn trước danh sách không thành một mục của danh sách.
 */
export function docDoan(chu: string): KhoiDoan | null {
  const dong = chu
    .split('\n')
    .map((d) => d.replace(TIEU_DE, ''))
    .filter((d) => d.trim() && !KE_NGANG.test(d));
  if (!dong.length) return null;

  const hang = dong.filter((d) => HANG_BANG.test(d) && !NGAN_BANG.test(d));
  if (hang.length >= 2 && hang.length >= dong.length - 1) {
    const o = hang.map((d) =>
      d.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((x) => x.trim()),
    );
    const [dau = [], ...than] = o;
    return { loai: 'bang', dau, than };
  }

  if (dong.some((d) => GACH_DAU_DONG.test(d))) {
    const cum: { gach: boolean; dong: string[] }[] = [];
    for (const d of dong) {
      const gach = GACH_DAU_DONG.test(d);
      const cuoi = cum[cum.length - 1];
      const noi = gach ? d.replace(GACH_DAU_DONG, '') : d;
      if (cuoi && cuoi.gach === gach) cuoi.dong.push(noi);
      else cum.push({ gach, dong: [noi] });
    }
    return { loai: 'cum', cum };
  }

  return { loai: 'chu', dong };
}

/** Mảnh trong một dòng: chữ thường, đậm, nghiêng, liên kết. */
export type Manh =
  | { loai: 'chu'; chu: string }
  | { loai: 'dam'; manh: Manh[] }
  | { loai: 'nghieng'; manh: Manh[] }
  | { loai: 'lien_ket'; url: string; duoi: string };

function tachLienKet(s: string): Manh[] {
  const ra: Manh[] = [];
  s.split(LIEN_KET).forEach((phan, i) => {
    if (i % 2 === 0) {
      if (phan) ra.push({ loai: 'chu', chu: phan });
      return;
    }
    const duoi = phan.match(DUOI_CAU)?.[0] ?? '';
    ra.push({ loai: 'lien_ket', url: duoi ? phan.slice(0, -duoi.length) : phan, duoi });
  });
  return ra;
}

function tachNghieng(s: string): Manh[] {
  const ra: Manh[] = [];
  s.split(/\*(.+?)\*/g).forEach((phan, i) => {
    if (i % 2) ra.push({ loai: 'nghieng', manh: tachLienKet(phan) });
    else ra.push(...tachLienKet(phan));
  });
  return ra;
}

/** `**` xử TRƯỚC `*`, nếu không bộ nghiêng ăn mất một nửa dấu sao đôi. Dấu sao mở không có dấu đóng về lại chữ thường. */
export function tachManh(s: string): Manh[] {
  const ra: Manh[] = [];
  s.split(/\*\*(.+?)\*\*/g).forEach((phan, i) => {
    if (i % 2) ra.push({ loai: 'dam', manh: tachNghieng(phan) });
    else ra.push(...tachNghieng(phan));
  });
  return ra;
}
